/**
 * Utilitario para el manejo y decodificación de tokens JWT.
 * Sigue el patrón estático de utilitarios establecido en el proyecto (ej. `CoordinateFormatterUtil`).
 */
export class JwtUtil {
  /**
   * Extrae la expiración (ms) de un JWT (claim `exp`, en segundos).
   * Devuelve `null` si el token no es un JWT decodificable o no tiene `exp`.
   */
  static getExpiryMs(token: string | null | undefined): number | null {
    const data = JwtUtil.decodePayload(token);
    return typeof data?.exp === 'number' && Number.isFinite(data.exp) ? data.exp * 1000 : null;
  }

  /**
   * Indica si el token proviene del endpoint simulado (claim `simulated`).
   * Los tokens reales no lo traen; permite a la capa de presentación
   * distinguir una sesión de demostración sin acoplarse a infrastructure.
   */
  static isSimulated(token: string | null | undefined): boolean {
    return JwtUtil.decodePayload(token)?.simulated === true;
  }

  private static decodePayload(token: string | null | undefined): Record<string, unknown> | null {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
      const json = new TextDecoder().decode(bytes);
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
}
